import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { EclassAnswerService, EclassAttachmentService } from '@app/core';
import { Answer, Attachment, TypeAttachment } from '@app/core/models';
import { SHARED_MODULES } from '@app/shared';
import { downloadFile, fileSvg, notNull, safeEmpty } from '@app/shared/utils';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, map } from 'rxjs';
import { ModalReviewService } from './modal-review.service';

interface AttachmentData extends Attachment {
  is_selected: boolean;
}

@Component({
  selector: 'app-modal-review-files',
  imports: [SHARED_MODULES],
  templateUrl: './modal-review-files.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalReviewFilesComponent {
  private readonly _modalReviewService = inject(ModalReviewService);
  private readonly _answerService = inject(EclassAnswerService);
  private readonly _attachmentService = inject(EclassAttachmentService);
  @Output() public select = new EventEmitter<Attachment>();
  public attachments: AttachmentData[];
  public answerId: string;
  public answer$ = new BehaviorSubject<Answer>(undefined);

  constructor() {
    this._modalReviewService.answers$
      .pipe(
        notNull(),
        map((answers) => answers.find((answer) => answer.is_selected)),
      )
      .subscribe((answer) => {
        this.answerId = answer?.id;
        const attachmentSelected = cloneDeep(this.attachmentSelected);
        if (attachmentSelected) {
          this.attachments = answer?.attachments?.map((attachment) => ({
            ...attachment,
            is_selected: attachment.id === attachmentSelected.id,
          }));
        } else {
          this.attachments = answer?.attachments?.map((attachment, index) => ({
            ...attachment,
            is_selected: index === 0,
          }));
        }
        this.answer$.next(answer);
      });
  }

  public get attachmentSelected(): AttachmentData {
    return this.attachments?.find((attachment) => attachment.is_selected);
  }

  @Input('attachmentIdSelected') public set setAttachmentIdSelected(attachmentId: number) {
    this.attachments = this.attachments?.map((attachment) => ({
      ...attachment,
      is_selected: attachment.id === attachmentId,
    }));
    this.markAsViewed(this.attachments?.find((attachment) => attachment.id === attachmentId));
  }

  public onGetIcon(extension: string): string {
    return fileSvg(extension);
  }

  public onSelect(attachment: AttachmentData) {
    this.select.emit(attachment);
  }

  public onDownloadFile(attachment: Attachment) {
    downloadFile(attachment);
  }

  private markAsViewed(attachment: Attachment) {
    if (!attachment) {
      return;
    }

    if (!attachment.viewed_at) {
      if (attachment.type === TypeAttachment.HTML) {
        this.markAnswerAsViewed();
      } else {
        this.markAttachmentAsViewed(attachment.id);
      }
    }
  }

  private markAnswerAsViewed() {
    this._answerService
      .viewed(this.answerId)
      .pipe(safeEmpty())
      .subscribe((viewedAt: string) =>
        this._modalReviewService.markAttachmentAsViewed(this.answerId, undefined, viewedAt),
      );
  }

  private markAttachmentAsViewed(attachmentId: number) {
    this._attachmentService
      .viewed(attachmentId)
      .pipe(safeEmpty())
      .subscribe((viewedAt: string) =>
        this._modalReviewService.markAttachmentAsViewed(this.answerId, attachmentId, viewedAt),
      );
  }
}
